/**
 * ManageMemorialTributesDialog — approve or hide guest tributes (moderation on by default).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, EyeOff, Loader2, MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';

const API = getApiBase();

export function ManageMemorialTributesDialog({ open, onOpenChange, memorialId, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [tributes, setTributes] = useState([]);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    if (!memorialId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/memorials-api/${memorialId}/tributes`);
      setTributes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load tributes.');
    } finally {
      setLoading(false);
    }
  }, [memorialId]);

  useEffect(() => {
    if (open && memorialId) load();
  }, [open, memorialId, load]);

  const moderate = async (tributeId, status) => {
    setActingId(tributeId);
    try {
      await axios.patch(`${API}/memorials-api/${memorialId}/tributes/${tributeId}`, { status });
      toast.success(status === 'approved' ? 'Tribute approved.' : 'Tribute hidden.');
      await load();
      onUpdated?.();
    } catch {
      toast.error('Could not update tribute.');
    } finally {
      setActingId(null);
    }
  };

  const pending = tributes.filter((t) => t.status === 'pending');
  const approved = tributes.filter((t) => t.status === 'approved');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="memorial-tributes-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif font-light">Manage tributes</DialogTitle>
          <DialogDescription>
            New tributes stay pending until you approve them. Nothing is shown publicly without your review.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {pending.length > 0 ? (
              <section>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-amber-700">
                  Pending review ({pending.length})
                </p>
                <ul className="space-y-3">
                  {pending.map((t) => (
                    <li key={t.id} className="rounded-xl border border-amber-200/60 bg-amber-50/40 p-4">
                      <p className="text-sm font-medium">{t.author_name}</p>
                      {t.relationship ? (
                        <p className="text-xs text-muted-foreground">{t.relationship}</p>
                      ) : null}
                      <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{t.message}</p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="rounded-full"
                          disabled={actingId === t.id}
                          onClick={() => moderate(t.id, 'approved')}
                        >
                          {actingId === t.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                          Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          disabled={actingId === t.id}
                          onClick={() => moderate(t.id, 'hidden')}
                        >
                          <EyeOff className="mr-1 h-3 w-3" />
                          Hide
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Approved ({approved.length})
              </p>
              {approved.length === 0 ? (
                <p className="text-sm text-muted-foreground">Approved messages from family will appear here.</p>
              ) : (
                <ul className="space-y-3">
                  {approved.map((t) => (
                    <li key={t.id} className="rounded-xl border border-border/50 bg-muted/10 p-4">
                      <p className="text-sm font-medium">{t.author_name}</p>
                      <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{t.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {pending.length === 0 && approved.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
                <MessageSquare className="h-8 w-8 opacity-40" />
                <p className="text-sm">When visitors leave messages, they&apos;ll wait here for your review.</p>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ManageMemorialTributesDialog;
